import React from 'react';
import { useTranslation } from 'react-i18next';
import { Row, Section } from '../settingsPrimitives';
import CompactSelectPicker from '../ui/CompactSelectPicker';
import { usePreferencesStore } from '../../store/preferencesStore';
import { normalizeDbIdleDisconnect } from '../../extensions/dbIdleDisconnect';
import DbTypeColorSettings from './DbTypeColorSettings';

/**
 * DB Manager settings, under Settings → Extensions → DB Manager.
 *
 * "Disconnect when idle": how long the DB Manager may sit in the background
 * (another panel open) before its open connections are closed. It used to be a
 * fixed 60 s (owner, 30.09.2026: "should be 30 min or 1 hour, configurable,
 * also do not disconnect"). The value applies at once — even to a DB Manager
 * that is already in the background. See extensions/dbIdleDisconnect.js.
 */
export default function DbManagerExtensionSettings() {
  const { t } = useTranslation();
  const value = usePreferencesStore((s) => s.dbIdleDisconnect);
  const update = usePreferencesStore((s) => s.updatePreference);
  const label = t('dbManagerExt.idleDisconnect', 'Disconnect when idle');

  return (
    <>
    <Section title={t('dbManagerExt.connectionsSection', 'Connections')}>
      <Row
        label={label}
        desc={t(
          'dbManagerExt.idleDisconnectDesc',
          'Close open database connections after the DB Manager has been in the background this long. Closing the app always disconnects.',
        )}
      >
        <CompactSelectPicker
          aria-label={label}
          value={normalizeDbIdleDisconnect(value)}
          onChange={(v) => update('dbIdleDisconnect', normalizeDbIdleDisconnect(v))}
          menuMinWidth={220}
          options={[
            { value: '15m', label: t('dbManagerExt.idle15m', 'After 15 minutes') },
            { value: '30m', label: t('dbManagerExt.idle30m', 'After 30 minutes (default)') },
            { value: '1h', label: t('dbManagerExt.idle1h', 'After 1 hour') },
            { value: '2h', label: t('dbManagerExt.idle2h', 'After 2 hours') },
            { value: 'never', label: t('dbManagerExt.idleNever', 'Never — keep connections open') },
          ]}
        />
      </Row>
    </Section>
    <DbTypeColorSettings />
    </>
  );
}
